import { Injectable, NgZone } from '@angular/core';
import { environment } from './../../../environments/environment';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LayoutService } from '../../layout/layout.service';
import { DomainService } from '../../domain/domain.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly JWT_TOKEN = 'token';

  API_SERVER = environment.apiUrl;
  API_SERVER_SECURED = `${environment.apiUrl}/secured`;

  private _loggedIn$ = new BehaviorSubject<boolean>(
    !!(typeof localStorage !== 'undefined' && localStorage.getItem(this.JWT_TOKEN))
  );
  public readonly loggedIn$ = this._loggedIn$.asObservable();

  private _authChanged = new BehaviorSubject<number>(Date.now());
  public readonly authChanged$ = this._authChanged.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private toastrService: ToastrService,
    private layout: LayoutService,
    private domainService: DomainService,
    private ngZone: NgZone
  ) {}

  checkAccount(cParams: Record<string, any> = {}): Observable<any> {
    const params = this.getHttpParams(cParams);

    return this.http
      .get(`${this.API_SERVER}/user`, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  hasMembership(spaceId: number | null | undefined): boolean {
    if (!spaceId) return false;

    const token = this.getJwtToken();
    if (!token) return false;

    try {
      const payload = this.decodeJwtPayload(token);
      const arr = Array.isArray(payload?.jobseekers) ? payload.jobseekers : [];

      return arr.some((js: any) => Number(js?.space_id) === Number(spaceId));
    } catch {
      return false;
    }
  }

  login(user: any): Observable<any> {
    return this.http.post<any>(`${this.API_SERVER}/login`, user).pipe(
      tap((res) => {
        if (
          res?.data?.pwd_force_change ||
          res?.data?.is_blocked ||
          res?.data?.no_plan ||
          res?.data?.licenseExpired
        ) {
          if (res?.data) delete res.data.token;

          this.toastrService.warning(
            'Login requires attention (e.g., blocked, expired license)'
          );

          return;
        }

        const regHandoff = sessionStorage.getItem('regHandoff') === '1';

        if (regHandoff) {
          const token = res?.data?.token;

          if (token) {
            localStorage.setItem(this.JWT_TOKEN, token);
          }

          const sidFromJwt = this.getSpaceIdFromToken();
          if (sidFromJwt) {
            localStorage.setItem('spaceID', String(sidFromJwt));
          }

          this.promotePendingCommunityContextIfAny();
          return;
        }

        this.clearOldAccountCacheBeforeLogin();

        this.doLoginUser(res.data);

        const sidFromJwt = this.getSpaceIdFromToken();
        if (sidFromJwt) {
          localStorage.setItem('spaceID', String(sidFromJwt));
        }

        this.promotePendingCommunityContextIfAny();

        this.emitAuthChange();

        this.ngZone.run(() => {
          this.layout.forceShowLayout?.();
          this._loggedIn$.next(true);

          const slug = localStorage.getItem('slug');
          const communityId = localStorage.getItem('communityId');

          if (this.domainService.isCommunityDomain() && slug && communityId) {
            this.router.navigate([`/${slug}/${communityId}/home`], {
              replaceUrl: true,
            });
          } else {
            this.router.navigate(['/profile'], { replaceUrl: true });
          }
        });
      }),
      catchError((error) => {
        console.error('❌ API Error:', error);
        return throwError(() => error);
      })
    );
  }

  private clearOldAccountCacheBeforeLogin(): void {
    localStorage.removeItem('spaceID');
    localStorage.removeItem('licenses');
    localStorage.removeItem('navigations');
    localStorage.removeItem('routes');
    localStorage.removeItem('role');
    localStorage.removeItem('products');
  }

  private promotePendingCommunityContextIfAny(): void {
    const pSlug = sessionStorage.getItem('pendingSlug');
    const pCid = sessionStorage.getItem('pendingCommunityId');
    const pSid = sessionStorage.getItem('pendingSpaceID');

    if (pSlug) localStorage.setItem('slug', pSlug);
    if (pCid) localStorage.setItem('communityId', pCid);
    if (pSid) localStorage.setItem('spaceID', pSid);

    sessionStorage.removeItem('pendingSlug');
    sessionStorage.removeItem('pendingCommunityId');
    sessionStorage.removeItem('pendingSpaceID');
  }

  doLoginUser(data: any): void {
    if (data?.token) {
      this.storeToken(data.token);
    }

    if (data?.licenses) {
      this.storeData(
        'licenses',
        String(data.licenses.join?.() ?? data.licenses)
      );
    }

    if (data?.navigations) {
      this.storeData(
        'navigations',
        String(data.navigations.join?.() ?? data.navigations)
      );
    }

    if (data?.routes) {
      this.storeData('routes', String(data.routes.join?.() ?? data.routes));
    }

    if (data?.role) {
      this.storeData('role', data.role);
    }

    if (data?.products) {
      this.storeData(
        'products',
        String(data.products.join?.() ?? data.products)
      );
    }
  }

  storeToken(token: string): void {
    const regHandoff = sessionStorage.getItem('regHandoff') === '1';

    localStorage.setItem(this.JWT_TOKEN, token);

    if (!regHandoff) {
      this.ngZone.run(() => this._loggedIn$.next(true));
    }
  }

  setToken(token: string): void {
    this.clearOldAccountCacheBeforeLogin();

    this.storeToken(token);

    const sidFromJwt = this.getSpaceIdFromToken();
    if (sidFromJwt) {
      localStorage.setItem('spaceID', String(sidFromJwt));
    }

    this.emitAuthChange();
  }

  emitAuthChange(): void {
    this._authChanged.next(Date.now());
  }

  storeData(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  getDataFromStore(key: string): string | null {
    return localStorage.getItem(key);
  }

  isLoggedIn(): boolean {
    return !!this.getJwtToken();
  }

  getJwtToken(): string {
    return typeof localStorage !== 'undefined'
      ? localStorage.getItem(this.JWT_TOKEN) || ''
      : '';
  }

  logout(deviceToken: string): Observable<any> {
    const token = this.getJwtToken();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    const body = { device_token: deviceToken };

    return this.http
      .post<any>(`${this.API_SERVER_SECURED}/logout`, body, { headers })
      .pipe(
        tap((res: any) => {
          this.toastrService.success(res?.data);
          this.finalizeLogout();
        }),
        catchError((error) => {
          console.error('❌ Logout API Error:', error);
          this.finalizeLogout();
          return throwError(() => error);
        })
      );
  }

  handleLogout(): void {
    const deviceToken = localStorage.getItem(this.JWT_TOKEN);

    if (!deviceToken) {
      this.finalizeLogout();
      return;
    }

    this.logout(deviceToken).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  private finalizeLogout(): void {
    let currSlug: string | null = null;
    let currCid: string | null = null;

    try {
      const m = window.location.pathname.match(/^\/([^\/]+)\/([^\/]+)/);
      currSlug = m?.[1] ?? null;
      currCid = m?.[2] ?? null;
    } catch {}

    const slug = currSlug || localStorage.getItem('slug') || null;
    const communityId = currCid || localStorage.getItem('communityId') || null;

    this.removeTokens();
    this.layout.resetLayout();

    sessionStorage.removeItem('pendingSlug');
    sessionStorage.removeItem('pendingCommunityId');
    sessionStorage.removeItem('pendingSpaceID');
    sessionStorage.removeItem('switchingCommunity');

    this.ngZone.run(() => {
      if (this.domainService.isCommunityDomain() && slug && communityId) {
        this.router.navigate([`/${slug}/${communityId}/login`]);
      } else {
        this.router.navigate(['/login']);
      }
    });
  }

  removeTokens(): void {
    localStorage.removeItem(this.JWT_TOKEN);
    localStorage.removeItem('spaceID');
    localStorage.removeItem('licenses');
    localStorage.removeItem('navigations');
    localStorage.removeItem('routes');
    localStorage.removeItem('role');
    localStorage.removeItem('products');

    if (!this.domainService.isCommunityDomain()) {
      localStorage.removeItem('slug');
      localStorage.removeItem('communityId');
    }

    this.ngZone.run(() => this._loggedIn$.next(false));
    this.emitAuthChange();
  }

  getUserId(): number | null {
    const token = this.getJwtToken();
    if (!token) return null;

    try {
      const payload = this.decodeJwtPayload(token);
      return payload?.userid ?? payload?.user_id ?? null;
    } catch {
      return null;
    }
  }

  getSpaceIdFromToken(): number | null {
    const token = this.getJwtToken();
    if (!token) return null;

    try {
      const payload = this.decodeJwtPayload(token);

      let sid = payload?.spaceID ?? payload?.space_id ?? null;

      if (
        sid == null &&
        Array.isArray(payload?.jobseekers) &&
        payload.jobseekers.length
      ) {
        sid = payload.jobseekers[0]?.space_id ?? null;
      }

      const n = Number(sid);

      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }

  private decodeJwtPayload(token: string): any {
    const part = token.split('.')[1] || '';
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64);

    return JSON.parse(json);
  }

  private extractData(res: any): any {
    return res || {};
  }

  private getHttpParams(cParams: any): HttpParams {
    let params = new HttpParams();

    Object.keys(cParams || {}).forEach((k) => {
      params = params.set(k, cParams[k]);
    });

    return params;
  }
}